import { FC, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { ArrowRight } from 'lucide-react';
import { Button } from 'primereact/button';
import { TeacherDashboard } from '../TeacherDashboard';
import TeacherWelcomeWizard from './TeacherWelcomeWizard';
import TeacherWelcomeSummary from './TeacherWelcomeSummary';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { selectUser } from '../../../redux/reducers/userSlice';
import { setTeacherOnboardingActive } from '../../../redux/reducers/teacherIntroSlice';
import { useHasTeacherSkippedOrCompletedIntro } from '../../../businessLogicService/teacherIntroStorage';
import DashboardNetworkAdminOrNoStudentsRostered from '../../../pages/DashboardNetworkAdminOrNoStudentsRostered';
import { useUserActionMutation } from '../../../custom-hooks/useUserActionMutation';

interface Props {
  isNetworkAdmin: boolean | undefined;
}

type WelcomeView = 'intro' | 'wizard' | 'summary' | 'dashboard';

const fade = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: { duration: 0.4, ease: 'easeInOut' as const },
};

const TeacherWelcomeWrapper: FC<Props> = ({ isNetworkAdmin }) => {
  const dispatch = useAppDispatch();
  const userId = useAppSelector(selectUser)?.id;
  const hasUserClassWithActiveStudent = useAppSelector(selectUser)?.hasUserClassWithActiveStudent;
  const [commitMutation] = useUserActionMutation();
  const introAlreadyDone = useHasTeacherSkippedOrCompletedIntro(userId);

  const [view, setView] = useState<WelcomeView>(introAlreadyDone ? 'dashboard' : 'intro');

  useEffect(() => {
    dispatch(setTeacherOnboardingActive(view !== 'dashboard'));
    return () => {
      dispatch(setTeacherOnboardingActive(false));
    };
  }, [view, dispatch]);

  const goToDashboard = (type: 'skipped' | 'completed') => {
    if (type === 'skipped') {
      commitMutation({
        variables: {
          input: {
            userAction: {
              userId: `UserNode:${userId}`,
              actionTypeId: 'ActionTypeNode:16',
              timeStamp: new Date().toISOString(),
              description: 'Welcome Screen Skipped',
            },
          },
        },
      });
    } else if (type === 'completed') {
      commitMutation({
        variables: {
          input: {
            userAction: {
              userId: `UserNode:${userId}`,
              actionTypeId: 'ActionTypeNode:15',
              timeStamp: new Date().toISOString(),
              description: 'Welcome Screen Completed',
            },
          },
        },
      });
    }
    setView('dashboard');
  };

  if (view === 'dashboard') {
    if (isNetworkAdmin === false)
      return <TeacherDashboard hasUserClassWithActiveStudent={hasUserClassWithActiveStudent} />;
    if (isNetworkAdmin === true) return <DashboardNetworkAdminOrNoStudentsRostered />;
  }

  return (
    <AnimatePresence mode="sync">
      {view === 'intro' && (
        <>
          <motion.div
            key="intro"
            {...fade}
            className="lg:tw-mx-[232px] tw-mx-[50px] tw-ml-auto tw-justify-end lg:tw-mt-[88px] tw-mt-[20px]"
          >
            <Button
              className="tw-flex tw-flex-row-reverse tw-gap-x-2 !tw-text-neutral-800 tw-ml-auto !tw-mt-6 tw-mx-[50px]"
              label="Skip intro"
              icon={<ArrowRight size={16} className="tw-text-neutral-800" />}
              link
              onClick={() => goToDashboard('skipped')}
            />
          </motion.div>
          <motion.div
            key="intro"
            {...fade}
            className="lg:tw-mx-[232px] tw-mx-[50px] tw-flex tw-flex-col lg:tw-gap-y-[100px] tw-h-[60vh] tw-justify-center"
          >
            <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-y-[24px]">
              <h1 className="tw-font-sora tw-text-black tw-font-bold tw-text-3xl">Hi!</h1>
              <h1 className="tw-font-sora tw-text-indigo-600 tw-font-bold tw-text-4xl">So happy you’re here.</h1>
              <p className="tw-font-atkinson tw-text-black tw-text-2xl tw-m-0 tw-text-center">
                Your account is all set. But before you dive in, here&apos;s<br></br> a quick look at{' '}
                <span className="tw-font-bold">how Roadmaps works</span>.
              </p>
              <Button
                className="tw-flex tw-flex-row-reverse tw-gap-x-2"
                label="Continue"
                icon={<ArrowRight size={16} />}
                onClick={() => setView('wizard')}
              />
            </div>
          </motion.div>
        </>
      )}

      {view === 'wizard' && (
        <motion.div key="wizard" {...fade}>
          <TeacherWelcomeWizard
            onSkip={() => goToDashboard('skipped')}
            onBack={() => setView('intro')}
            onComplete={() => setView('summary')}
          />
        </motion.div>
      )}

      {view === 'summary' && (
        <motion.div key="summary" {...fade}>
          <TeacherWelcomeSummary onFinish={() => goToDashboard('completed')} />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default TeacherWelcomeWrapper;
